import React, { useState, useEffect, useContext } from "react";
import { CSVLink } from "react-csv";
import { addDays } from "date-fns";
import restService from "../../../restService";
import { DatePicker } from "../../components";
import Table from "./table";
import UserContext from "../../../contexts/UserContext";
import CampaignContext from "../../../contexts/CampaignContext";
import moment from "moment";
import TablePagination from "./TablePagination";
import { PulseLoader } from "react-spinners";

const ExportData = ({ entries }) => {
  const [csvData, setCsvData] = useState([]);

  // :::: EXPORT TRANSACTIONS :::::
  useEffect(() => {
    // EXPORTING THE DATA INTO CSV FILE
    const toCSV = [];
    for (let i = 0; i < entries?.length; i++) {
      let obj = {};

      obj["date"] = entries[i].date
        ? moment(new Date(entries[i].date)).format("DD/MM/YYYY, h:mm:ss A")
        : "";

      obj["msisdn"] = entries[i].data?.msisdn;
      obj["association"] = entries[i].data?.association;
      obj["name"] = entries[i].data?.name;
      obj["decoderNumber"] = entries[i].data?.decoderNumber;
      obj["region"] = entries[i].data?.region;

      toCSV.push(obj);
    }

    setCsvData(toCSV);
  }, [entries]);

  const reportHeader = [
    { label: "Date", key: "date" },
    { label: "Phone number", key: "msisdn" },
    { label: "Association", key: "association" },
    { label: "Name", key: "name" },
    { label: "Decoder number", key: "decoderNumber" },
    { label: "Region", key: "region" },
  ];

  const csvFile = {
    filename: "survey.csv",
    headers: reportHeader,
    data: csvData,
  };

  return (
    <>
      <CSVLink
        {...csvFile}
        className="flex items-center border rounded border-[#3989DD] text-xs md:text-base py-[6px] px-4 transition hover:bg-[#3989DD1a] hover:shadow-lg"
        type="button"
      >
        <span className="px-1 pb-1">
          <svg
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_371_37)">
              <path
                d="M6 0.5625V7.875"
                stroke="currentColor"
                strokeMiterlimit="10"
              />
              <path
                d="M4.125 2.4375L6 0.5625L7.875 2.4375"
                stroke="currentColor"
                strokeMiterlimit="10"
                // strokeMiterlimit="square"
              />
              <path
                d="M7.5 4.875H9.375C9.62364 4.875 9.8621 4.97377 10.0379 5.14959C10.2137 5.3254 10.3125 5.56386 10.3125 5.8125V10.5C10.3125 10.7486 10.2137 10.9871 10.0379 11.1629C9.8621 11.3387 9.62364 11.4375 9.375 11.4375H2.625C2.37636 11.4375 2.1379 11.3387 1.96209 11.1629C1.78627 10.9871 1.6875 10.7486 1.6875 10.5V5.8125C1.6875 5.56386 1.78627 5.3254 1.96209 5.14959C2.1379 4.97377 2.37636 4.875 2.625 4.875H4.5"
                stroke="currentColor"
                strokeMiterlimit="10"
                // strokeMiterlimit="square"
              />
            </g>
            <defs>
              <clipPath id="clip0_371_37">
                <rect width="12" height="12" fill="currentColor" />
              </clipPath>
            </defs>
          </svg>
        </span>

        <span className="text-base px-1.5 hidden md:block">Export Entries</span>
      </CSVLink>
    </>
  );
};

const SurveyResponse = () => {
  const [campaign] = useContext(CampaignContext);
  const [user] = useContext(UserContext);
  const [dateRange, setDateRange] = useState([
    {
      startDate: addDays(new Date(), -365).setHours(0, 0, 0, 0),
      endDate: new Date().setHours(23, 59, 59, 0),
      key: "selection",
    },
  ]);
  const [entries, setEntries] = useState([]);
  const [dataToExport, setDataToExport] = useState([]);
  const [tableHeader, setTableHeader] = useState([]);

  const [loading, setLoading] = useState(false);
  const [queryNumber, setQueryNumber] = useState("");
  const [queryString, setQueryString] = useState("");

  const [page, setPage] = useState(0);
  const [allEntries, setAllEntries] = useState(0);
  const [pages, setPages] = useState(1);
  const [size, setSize] = useState(20);

  useEffect(() => {
    // getSurveyReponse();
    getFilteredSurveyReponse();
  }, [page, size, queryNumber, queryString, dateRange]);

  const getFilteredSurveyReponse = async () => {
    setLoading(true);

    try {
      const {
        data: { content, totalPages, numberOfElements },
      } = await restService.Survey.filterSurveyResponse({
        campaignId: campaign.id,
        page: page,
        limit: size,
        msisdn: queryNumber,
        endDate: new Date(dateRange[0].endDate).toISOString(),
        startDate: new Date(dateRange[0].startDate).toISOString(),
        name: queryString,
        decoderNumber: queryNumber,
        token: user.accessToken,
      });

      const res = content;
      const keys = res.length > 0 ? Object.keys(res[0]?.data) : [];

      setPages(totalPages);

      setTableHeader(keys);
      setEntries(res);
      setAllEntries(numberOfElements);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getDataToExport = async () => {
    try {
      const {
        data: { content },
      } = await restService.Survey.filterSurveyResponse({
        campaignId: campaign.id,
        page: page,
        limit: 10000000,
        msisdn: queryNumber,
        endDate: new Date(dateRange[0].endDate).toISOString(),
        startDate: new Date(dateRange[0].startDate).toISOString(),
        name: queryString,
        decoderNumber: queryNumber,
        token: user.accessToken,
      });

      const res = content;
      setDataToExport(res);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getDataToExport();
  }, [queryNumber, queryString, dateRange]);

  const handleSearch = (value) => {
    // setSearchTerm(e);
    if (value && !isNaN(Number(value))) {
      setQueryString("");
      setQueryNumber(value);
    } else if (value && isNaN(Number(value))) {
      setQueryNumber("");
      setQueryString(value);
    } else {
      setQueryString("");
      setQueryNumber("");
    }
  };

  return (
    <>
      <div className="px-7 ">
        <h1 className="text-2xl leading-9 font-[600]">Survey Response</h1>
      </div>

      <div className="bg-white shadow rounded mt-8 px-7 py-5">
        <>
          <div className="flex md:items-center md:gap-5 gap-2 justify-between flex-col md:flex-row">
            <div className="flex md:items-center flex-col md:gap-5 gap-2 md:flex-row">
              <DatePicker state={dateRange} setState={setDateRange} />
              <div className="">
                <label className="relative block">
                  <span className="sr-only">Search</span>
                  <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 10 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.21879 9.21873L6.64941 6.64935"
                        stroke="#848484"
                        strokeMiterLimit="10"
                        // strokeMiterLimit="square"
                      />
                      <path
                        d="M4.21875 7.65625C6.11723 7.65625 7.65625 6.11723 7.65625 4.21875C7.65625 2.32027 6.11723 0.78125 4.21875 0.78125C2.32027 0.78125 0.78125 2.32027 0.78125 4.21875C0.78125 6.11723 2.32027 7.65625 4.21875 7.65625Z"
                        stroke="#848484"
                        strokeMiterLimit="10"
                        // strokeMiterLimit="square"
                      />
                    </svg>
                  </span>
                  <input
                    type="text"
                    placeholder="Search "
                    onChange={(e) => {
                      // setPage(0);
                      handleSearch(e.target.value);
                    }}
                    className="py-1.5 pl-8 w-full md:w-auto border-none focus:border-none focus:pr-10 focus:outline-none rounded-md bg-[#F4F4F4] "
                  />
                </label>
              </div>
            </div>

            <div className="flex items-end justify-end gap-3">
              <div className="bg-[#3989DD] text-white rounded">
                <ExportData entries={dataToExport} />
              </div>
              {/* <button onClick={() => setIsRunDraw(true)}>
                    Export Entries
                  </button> */}

              <div>
                {/* <button
                      onClick={() => setIsRunDraw(true)}
                      className="text-white w-full md:w-auto flex-1 bg-[#3989DD] border border-[#3989DD] text-xs md:text-base py-[6px] px-4 transition hover:bg-[#235080] hover:border-[#235080] rounded"
                    >
                      Select Winners
                    </button> */}
              </div>
            </div>
          </div>

          {loading ? (
            <>
              <div className="h-full w-full flex items-center justify-center">
                <PulseLoader color="#FF6A3A" />
              </div>
            </>
          ) : (
            <>
              {" "}
              <div className="w-full overflow-auto mt-4">
                <Table tableHeader={tableHeader} entries={entries} />
              </div>
            </>
          )}

          <div className="mt-8">
            <TablePagination
              page={page}
              pages={pages}
              setPage={setPage}
              size={size}
              setSize={setSize}
              allEntries={allEntries}
            />
          </div>
        </>
      </div>
    </>
  );
};

export default SurveyResponse;
